import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
  { value: "customer", label: "Resident / Car Owner" },
  { value: "zypper", label: "Become a Zypper" },
  { value: "society", label: "Apartment / RWA" },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  society: "",
  city: "Bangalore",
  persona: "customer",
  message: "",
};

const INPUT_CLASS =
  "bg-zinc-950 border-white/10 text-white h-12 rounded-xl focus-visible:ring-[#FF5A00] focus-visible:ring-1 focus-visible:border-[#FF5A00] placeholder:text-zinc-600";

function PersonaPills({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2 mb-8 p-1 rounded-full bg-zinc-950 border border-white/5">
      {PERSONAS.map((p) => (
        <button
          key={p.value}
          type="button"
          data-testid={`persona-${p.value}`}
          onClick={() => onChange(p.value)}
          className={`py-3 px-2 rounded-full text-xs md:text-sm font-medium transition-all ${
            value === p.value
              ? "bg-[#FF5A00] text-white shadow-[0_0_20px_-5px_#FF5A00]"
              : "text-zinc-400 hover:text-white"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function FormField({ id, label, type = "text", value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label htmlFor={id} className="text-zinc-300 text-sm mb-2 block">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={INPUT_CLASS}
      />
    </div>
  );
}

function SuccessState({ onAddAnother }) {
  return (
    <div className="relative text-center py-10" data-testid="waitlist-success">
      <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#FF5A00]/15 border border-[#FF5A00]/30 mb-6">
        <CheckCircle2 size={32} className="text-[#FF5A00]" />
      </div>
      <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
        You&rsquo;re on the list.
      </h3>
      <p className="mt-4 text-zinc-400 max-w-md mx-auto">
        We&rsquo;ll reach out as soon as Zyp is live in your society. Keep an
        eye on your inbox — early access perks coming up.
      </p>
      <Button
        data-testid="waitlist-add-another"
        onClick={onAddAnother}
        variant="outline"
        className="mt-8 bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full"
      >
        Add another
      </Button>
    </div>
  );
}

function WaitlistHeader() {
  return (
    <div className="text-center mb-10">
      <div className="text-xs uppercase tracking-[0.22em] text-[#FF5A00] mb-4">
        Early access
      </div>
      <h2 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">
        Join the waitlist.
      </h2>
      <p className="mt-4 text-zinc-400 max-w-md mx-auto">
        Founding members get launch pricing locked for life and 2 free washes.
      </p>
    </div>
  );
}

function validateForm(form) {
  if (!form.name || !form.email || !form.phone) {
    return "Please fill in name, email and phone";
  }
  return null;
}

async function submitWaitlist(form) {
  await axios.post(`${API}/waitlist`, form);
}

export default function Waitlist() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, persona: e.detail }));
      const el = document.getElementById("waitlist-name");
      if (el) el.focus();
    };
    window.addEventListener("zyp-set-persona", handler);
    return () => window.removeEventListener("zyp-set-persona", handler);
  }, []);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const setPersona = (persona) => setForm((f) => ({ ...f, persona }));

  const resetForm = () => {
    setSubmitted(false);
    setForm(EMPTY_FORM);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    setLoading(true);
    try {
      await submitWaitlist(form);
      setSubmitted(true);
      toast.success("You're in. We'll reach out shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-form-section"
      className="relative py-24 md:py-32 px-6 md:px-12 border-t border-white/5"
    >
      <div className="absolute inset-0 zyp-glow opacity-60" />
      <div className="max-w-4xl mx-auto relative">
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900/60 to-black/60 backdrop-blur-xl p-8 md:p-14 overflow-hidden">
          <div className="absolute -top-32 -right-20 h-72 w-72 rounded-full bg-[#FF5A00]/20 blur-3xl pointer-events-none" />

          {submitted ? (
            <SuccessState onAddAnother={resetForm} />
          ) : (
            <div className="relative">
              <WaitlistHeader />
              <PersonaPills value={form.persona} onChange={setPersona} />

              <form
                onSubmit={onSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <FormField
                  id="waitlist-name"
                  label="Full name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  testId="waitlist-input-name"
                />
                <FormField
                  id="waitlist-email"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@apartment.com"
                  testId="waitlist-input-email"
                />
                <FormField
                  id="waitlist-phone"
                  label="Phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98xxxxxxxx"
                  testId="waitlist-input-phone"
                />
                <FormField
                  id="waitlist-society"
                  label={form.persona === "society" ? "Society name *" : "Society"}
                  value={form.society}
                  onChange={update("society")}
                  placeholder="e.g. Prestige Lakeside"
                  testId="waitlist-input-society"
                />

                <div className="md:col-span-2">
                  <FormField
                    id="waitlist-message"
                    label="Anything we should know? (optional)"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Car model, parking type, society size, etc."
                    testId="waitlist-input-message"
                  />
                </div>

                <div className="md:col-span-2 mt-2">
                  <Button
                    type="submit"
                    data-testid="waitlist-submit"
                    disabled={loading}
                    className="w-full bg-[#FF5A00] hover:bg-[#FF7A33] text-white h-14 rounded-full text-base font-medium group"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" strokeWidth={2.5} />
                        Joining…
                      </>
                    ) : (
                      <>
                        Claim my spot
                        <ArrowRight
                          size={18}
                          className="ml-1 group-hover:translate-x-1 transition-transform"
                        />
                      </>
                    )}
                  </Button>
                  <p className="text-center text-xs text-zinc-500 mt-4">
                    By joining, you agree to receive launch updates. No spam.
                    Unsubscribe anytime.
                  </p>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
